/**
 * DixonColesTable - Sortable table for Dixon-Coles fixtures
 */

import React from 'react';
import { DixonColesRow } from './DixonColesRow';
import { MatchAnalysis } from '../match-analysis';
import type { DixonColesFixture, DCSortColumn } from '../../types';

interface Props {
  fixtures: DixonColesFixture[];
  sortColumn: DCSortColumn;
  sortOrder: 'asc' | 'desc';
  onSort: (column: DCSortColumn) => void;
  expandedId: number | null;
  onToggleExpand: (fixtureId: number) => void;
}

const COLUMNS: { key: DCSortColumn; label: string; className?: string }[] = [
  { key: 'kickoff', label: 'Kickoff' },
  { key: 'home_win_prob', label: 'Home', className: 'col-prob' },
  { key: 'draw_prob', label: 'Draw', className: 'col-prob' },
  { key: 'away_win_prob', label: 'Away', className: 'col-prob' },
  { key: 'over_1_5', label: 'O1.5', className: 'col-prob hide-tablet' },
  { key: 'over_2_5', label: 'O2.5', className: 'col-prob' },
  { key: 'over_3_5', label: 'O3.5', className: 'col-prob hide-tablet' },
  { key: 'btts_yes', label: 'BTTS', className: 'col-prob' },
];

export function DixonColesTable({
  fixtures,
  sortColumn,
  sortOrder,
  onSort,
  expandedId,
  onToggleExpand,
}: Props) {
  return (
    <div className="dc-table-container">
      <table className="dc-table">
        <thead>
          <tr>
            <th className="col-kickoff">
              <SortableHeader
                column="kickoff"
                label="Kickoff"
                active={sortColumn === 'kickoff'}
                direction={sortOrder}
                onSort={onSort}
              />
            </th>
            <th className="col-match">Match</th>
            <th className="col-league">League</th>
            {COLUMNS.slice(1).map(col => (
              <th key={col.key} className={col.className}>
                <SortableHeader
                  column={col.key}
                  label={col.label}
                  active={sortColumn === col.key}
                  direction={sortOrder}
                  onSort={onSort}
                />
              </th>
            ))}
            <th className="col-expand" />
          </tr>
        </thead>
        <tbody>
          {fixtures.map(fixture => (
            <React.Fragment key={fixture.fixture_id}>
              <DixonColesRow
                fixture={fixture}
                isExpanded={expandedId === fixture.fixture_id}
                onToggle={() => onToggleExpand(fixture.fixture_id)}
              />
              {expandedId === fixture.fixture_id && (
                <tr className="dc-expanded-row">
                  <td colSpan={12}>
                    <MatchAnalysis
                      fixtureId={fixture.fixture_id}
                      homeTeam={{
                        id: fixture.home_team.id,
                        name: fixture.home_team.name,
                        crest_url: fixture.home_team.logo,
                      }}
                      awayTeam={{
                        id: fixture.away_team.id,
                        name: fixture.away_team.name,
                        crest_url: fixture.away_team.logo,
                      }}
                      leagueId={fixture.league.id}
                      leagueName={fixture.league.name}
                      leagueLogo={fixture.league.logo}
                    />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SortableHeader({
  column,
  label,
  active,
  direction,
  onSort,
}: {
  column: DCSortColumn;
  label: string;
  active: boolean;
  direction: 'asc' | 'desc';
  onSort: (column: DCSortColumn) => void;
}) {
  return (
    <button
      className={`sortable-header ${active ? 'active' : ''}`}
      onClick={() => onSort(column)}
      type="button"
    >
      <span>{label}</span>
      {active && (
        <svg
          className={`sort-icon ${direction}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="currentColor"
        >
          <path d="M6 2L10 7H2L6 2Z" />
        </svg>
      )}
    </button>
  );
}
